import React from 'react';
import './TradeTable.css';

function TradeTable({ trades, showGuru = false, showProduct = false }) {
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `${num.toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getReturnClass = (percent) => {
    if (percent === null || percent === undefined || percent === '') return '';
    const num = parseFloat(percent);
    if (isNaN(num)) return '';
    return num > 0 ? 'positive' : 'negative';
  };

  const getStatusClass = (trade) => {
    const status = trade.status?.toLowerCase() || '';
    // If closed with a loss (negative P/L), show as loss
    if (status === 'closed' && trade.option_profit_loss && parseFloat(trade.option_profit_loss) < 0) {
      return `${status} loss`;
    }
    return status;
  };

  // Smart field getters - use appropriate fields based on asset_type
  const getEntryPrice = (trade) => {
    const assetTypeLower = String(trade.asset_type || '').toLowerCase();
    const isStock = assetTypeLower === 'stock';
    const value = isStock ? trade.entry_price : trade.option_entry_price;

    // Debug for stocks
    if (isStock) {
      console.log(`Stock ${trade.symbol} Entry:`, {
        asset_type: trade.asset_type,
        entry_price: trade.entry_price,
        option_entry_price: trade.option_entry_price,
        returned: value
      });
    }
    return value;
  };

  const getExitPrice = (trade) => {
    const assetTypeLower = String(trade.asset_type || '').toLowerCase();
    const isStock = assetTypeLower === 'stock';
    const value = isStock ? trade.exit_price : trade.option_exit_price;
    return value;
  };

  const getPercentReturn = (trade) => {
    const assetTypeLower = String(trade.asset_type || '').toLowerCase();
    const isStock = assetTypeLower === 'stock';
    const value = isStock ? trade.percent_return : trade.option_percent_return;
    return value;
  };

  const getProfitLoss = (trade) => {
    // For stock trades, calculate P/L if we have prices
    const isStock = trade.asset_type?.toLowerCase() === 'stock';
    if (isStock) {
      if (trade.entry_price && trade.exit_price && trade.percent_return) {
        // Assuming standard $1000 investment for stocks
        const investment = 1000;
        return investment * (parseFloat(trade.percent_return) / 100);
      }
      return null;
    }
    return trade.option_profit_loss;
  };

  const formatAssetType = (assetType) => {
    if (!assetType) return '-';
    const typeMap = {
      'debit_spread': 'Debit Spread',
      'credit_spread': 'Credit Spread',
      'option': 'Option',
      'stock': 'Stock',
      'iron_condor': 'Iron Condor'
    };
    return typeMap[assetType.toLowerCase()] || assetType;
  };

  return (
    <div className="trade-table-container">
      <table className="trade-table">
        <thead>
          <tr>
            {showGuru && <th>Guru</th>}
            {showProduct && <th>Product</th>}
            <th>Trade #</th>
            <th>Symbol</th>
            <th>Asset Type</th>
            <th>Details</th>
            <th>Entry Date</th>
            <th>Exit Date</th>
            <th>Days Held</th>
            <th>Entry Price</th>
            <th>Exit Price</th>
            <th>P/L</th>
            <th>% Return</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {trades && trades.length > 0 ? (
            trades.map((trade) => (
              <tr key={trade.id}>
                {showGuru && (
                  <td className="guru-cell">{trade.guru_name}</td>
                )}
                {showProduct && (
                  <td className="product-cell">{trade.product_name || '-'}</td>
                )}
                <td>{trade.trade_number || '-'}</td>
                <td className="symbol-cell">{trade.symbol}</td>
                <td>
                  <span className={`asset-type-badge ${trade.asset_type || 'unknown'}`}>
                    {formatAssetType(trade.asset_type)}
                  </span>
                </td>
                <td className="option-data-cell">
                  {trade.asset_type?.toLowerCase() === 'stock' ? (
                    <span className="stock-details-text">Stock Assignment</span>
                  ) : trade.asset_type?.toLowerCase() === 'debit_spread' || trade.asset_type?.toLowerCase() === 'credit_spread' ? (
                    <span className="spread-details-text">
                      {trade.long_strike && trade.short_strike ? (
                        <>
                          <span className="strike">${parseFloat(trade.long_strike).toFixed(2)}</span>
                          <span className="separator">/</span>
                          <span className="strike">${parseFloat(trade.short_strike).toFixed(2)}</span>
                          {trade.option_type && (
                            <span className={`option-badge ${trade.option_type.toLowerCase()}`}>
                              {trade.option_type.toUpperCase()}
                            </span>
                          )}
                        </>
                      ) : trade.option_data || '-'}
                    </span>
                  ) : trade.option_data ? (
                    <span className="option-details-text">{trade.option_data}</span>
                  ) : '-'}
                </td>
                <td>{formatDate(trade.entry_date)}</td>
                <td>{formatDate(trade.exit_date)}</td>
                <td>{trade.days_held || '-'}</td>
                <td>{formatCurrency(getEntryPrice(trade))}</td>
                <td>{formatCurrency(getExitPrice(trade))}</td>
                <td className={getReturnClass(getProfitLoss(trade))}>
                  {formatCurrency(getProfitLoss(trade))}
                </td>
                <td className={getReturnClass(getPercentReturn(trade))}>
                  {formatPercent(getPercentReturn(trade))}
                </td>
                <td>
                  <span className={`status-badge ${getStatusClass(trade)}`}>
                    {trade.status}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={showGuru ? "16" : "15"} className="no-data">
                No trades found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default TradeTable;
